import {EntityType, HasId} from "../../@types/entity";

/**
 * Easy filter to grab unique values from an array of primitives.
 *
 * @param {any} value
 * @param {number} index
 * @param {any} self
 * @returns {boolean}
 */
export function distinctFilter(value: any, index: number, self: any): boolean {
    return self.indexOf(value) === index;
}

/**
 * Easy filter to drop falsy values from an array.
 *
 * OWA-554: a type guard (not just `: boolean`) so `arr.filter(existsFilter)` narrows the
 * element type. Runtime is unchanged: `!!value`, i.e. it drops ALL falsy values
 * (null/undefined/false AND 0/''/NaN). Every current caller filters object/entity arrays,
 * where the only falsy values are null/undefined/false, so the falsy-primitive distinction
 * never bites. NOTE: this differs from `util.existsFilter`, which keeps 0/'' (dropping only
 * null/undefined/false) — the two are interchangeable for object arrays but NOT for arrays
 * of numbers/strings.
 *
 * @param {any} value
 * @returns {boolean}
 */
export function existsFilter<T>(value: T): value is Exclude<NonNullable<T>, false> {
    return !!value;
}

/**
 * Item exists and status <> 'D'.
 *
 * OWA-554: a type guard mirroring `util.existsAndNotDeletedFilter`, so
 * `arr.filter(existsAndNotDeletedFilter)` narrows a nullable entity array to the entity
 * type. Runtime unchanged.
 *
 * @param {any} value
 * @returns {boolean}
 */
export function existsAndNotDeletedFilter<T extends EntityType>(value: T | null | undefined): value is T {
    return !!value && value.status !== 'D';
}

/**
 * Easy filter to ensure only one of each entity (by entity.id) is present.
 *
 * @param {HasId} value
 * @param {number} index
 * @param {HasId[]} self
 * @returns {boolean}
 */
export function distinctIdFilter(value: HasId, index: number, self: HasId[]): boolean {
    return self.slice(0, index + 1).filter(c => c.id === value.id).length === 1;
}

/**
 * Get rid of entities with the deleted flag set.
 *
 * @param {EntityType} value
 * @returns {boolean}
 */
export function deletedFilter(value: EntityType): boolean {
    return value.status !== 'D';
}
